import dayjs from "dayjs";

/**
 * Shared presentational helpers for the leave & overtime request/approval
 * components. Leave status ids (1=Pending, 2=Approved, 3=Declined) mirror
 * the backend's leave_status seed data.
 */

export function formatLeaveDateRange(leave) {
  const fromDate = dayjs(leave.from).format("MMM D, YYYY");
  const toDate = dayjs(leave.to).format("MMM D, YYYY");

  if (leave.is_half_day) {
    const period = leave.half_day_period ? ` (${leave.half_day_period})` : "";
    return `${fromDate}${period}`;
  }

  return leave.from === leave.to ? fromDate : `${fromDate} - ${toDate}`;
}

export function formatLeaveDuration(leave) {
  if (leave.is_half_day) {
    const period = leave.half_day_period === "AM" ? "Morning" : "Afternoon";
    return `0.5 day (${period})`;
  }
  const days = leave.count;
  return `${days} day${days > 1 ? "s" : ""}`;
}

export function getLeaveStatusColor(statusId) {
  switch (statusId) {
    case 1: return "orange";
    case 2: return "green";
    case 3: return "red";
    default: return "grey";
  }
}

export function getLeaveStatusIcon(statusId) {
  switch (statusId) {
    case 1: return "mdi-clock-outline";
    case 2: return "mdi-check-circle";
    case 3: return "mdi-close-circle";
    default: return "mdi-help-circle";
  }
}

export function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
}

// Approver choices for leave / overtime / WFH requests: company managers
// (company_access.is_manager) and team managers (user_role_managers). Each is
// shown by full name with where they manage, e.g.
// "Aaron Hussey — Company: DG Ventures · Team: Marketing". Managers who share a
// company or team with the requester are listed first.
const HIDDEN_TEAM_LABELS = ["super admin", "admin"];
// Always offered as approvers, even without a manager assignment. Matched by
// user id: the users API strips email for anyone but Super Admins.
const EXTRA_APPROVER_IDS = [122]; // Daniza (daniza@stagit.ie)

export function getCompanyManagerApprovers(users, currentUser, roleManagers = [], roles = []) {
  const list = Array.isArray(users) ? users : [];
  const meId = currentUser && currentUser.id;
  const me = list.find((u) => u.id === meId) || currentUser || {};
  const myCompanyIds = new Set((me.access || []).map((a) => a.company_id));
  const fullName = (u) => `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.name;

  const teamsByManager = {};
  (Array.isArray(roleManagers) ? roleManagers : []).forEach((rm) => {
    const role = (roles || []).find((r) => r.id === rm.user_role_id);
    const name = (rm.user_role && rm.user_role.name) || (role && role.name) || `Team #${rm.user_role_id}`;
    (teamsByManager[rm.manager_id] = teamsByManager[rm.manager_id] || []).push({ id: rm.user_role_id, name });
  });

  return list
    .filter((u) => u.id !== meId)
    .map((u) => {
      const managed = (u.access || []).filter((a) => a.is_manager);
      const companies = [...new Set(managed.map((a) => (a.company && a.company.name) || `Company #${a.company_id}`))];
      const allTeams = [...new Set((teamsByManager[u.id] || []).map((t) => t.name))];
      const extra = EXTRA_APPROVER_IDS.includes(Number(u.id));
      if (!companies.length && !allTeams.length && !extra) return null;
      // Super Admin / Admin are access levels, not real teams: still approvers, just not labelled
      const teams = allTeams.filter((n) => !HIDDEN_TEAM_LABELS.includes(String(n).trim().toLowerCase()));
      const parts = [];
      if (companies.length) parts.push(`Company: ${companies.join(", ")}`);
      if (teams.length) parts.push(`Team: ${teams.join(", ")}`);
      const shared =
        managed.some((a) => myCompanyIds.has(a.company_id)) ||
        (teamsByManager[u.id] || []).some((t) => t.id === me.role_id);
      return { ...u, approver_label: parts.length ? `${fullName(u)} — ${parts.join(" · ")}` : fullName(u), _shared: shared };
    })
    .filter(Boolean)
    .sort((x, y) => Number(y._shared) - Number(x._shared) || x.approver_label.localeCompare(y.approver_label));
}

export function getOvertimeStatusColor(statusName) {
  const name = (statusName || "").toLowerCase();
  if (name.includes("approv")) return "green";
  if (name.includes("declin") || name.includes("reject")) return "red";
  return "orange";
}

export function getOvertimeStatusIcon(statusName) {
  const name = (statusName || "").toLowerCase();
  if (name.includes("approv")) return "mdi-check-circle";
  if (name.includes("declin") || name.includes("reject")) return "mdi-close-circle";
  return "mdi-clock-outline";
}
